import { KeyboardEvent } from 'react';
import { LOGOUT_MODAL, SPCP_ORIGIN, urlsToHideComponent } from './constants';
import { LocalStorageService } from 'br2s-ui-common-utils-02';
import { TODO_LINKS } from '@/pages/ToDo/todo-constants';
import sfv from '@/config/sfvTransactions_config.json';

export type MockURL = {
  pathname: string;
  search: string;
  hash: string;
  state?: any;
};

type AddressProperties =
  | 'blockNo'
  | 'streetName'
  | 'levelNo'
  | 'unitNo'
  | 'buildingName'
  | 'postalCode'
  | 'foreignAddr1'
  | 'foreignAddr2';

type AddressObject = {
  [key in AddressProperties]?: string;
};

export const handleKeyDown = (
  e: KeyboardEvent<HTMLElement>,
  fn: () => void | undefined,
) => {
  if (['Enter', 'Space'].includes(e.code)) {
    fn?.();
  }
};

export const getCurrentTime = () => {
  const currentTime: Date = new Date();
  const hours: number = currentTime.getHours();
  const minutes: number = currentTime.getMinutes();
  const seconds: number = currentTime.getSeconds();

  return {
    currentTime,
    hours,
    minutes,
    seconds,
  };
};

export const formatDate = (dateString: string) => {
  let date;
  // Check if format is DD/MM/YYYY
  if (dateString?.split('/').length === 3) {
    const [d, m, y] = dateString.split('/');
    date = new Date(Number(y), Number(m) - 1, Number(d));
  } else {
    date = new Date(dateString);
  }

  if (!Number.isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    const formattedDay = day < 10 ? '0' + day : day.toString();
    return `${formattedDay} ${month} ${year}`;
  }
  return '';
};

export const setInactivity = (isInactive: boolean) => {
  const formattedTime = `${getCurrentTime()
    .hours.toString()
    .padStart(2, '0')}:${getCurrentTime()
    .minutes.toString()
    .padStart(2, '0')}:${getCurrentTime().seconds.toString().padStart(2, '0')}`;

  const inactivity = { inactive: true, startAt: formattedTime };
  if (isInactive && localStorage.getItem('auth') !== null) {
    localStorage.setItem('inactivity', JSON.stringify(inactivity));
  } else {
    localStorage.removeItem('inactivity');
  }
};

export const isUserInactive = () => {
  const inactivityJSON = localStorage.getItem('inactivity');
  const inactivity = inactivityJSON ? JSON.parse(inactivityJSON) : null;
  const isInactive = inactivity ? inactivity.inactive : null;
  const startAt = inactivity ? inactivity.startAt : null;

  if (inactivityJSON) {
    const [startHours, startMinutes, startSeconds] = startAt
      .split(':')
      .map(Number);
    const startDate = new Date();
    startDate.setHours(startHours, startMinutes, startSeconds);

    const endDate = new Date();
    endDate.setHours(
      getCurrentTime().hours,
      getCurrentTime().minutes,
      getCurrentTime().seconds,
    );
    const timer = endDate.getTime() - startDate.getTime();

    return {
      isInactive,
      startAt,
      timer: LOGOUT_MODAL.LOGOUT_TIMER - timer,
    };
  }

  return {
    isInactive: false,
    startAt: null,
    timer: 0,
  };
};

export const getLogoutModalContent = (isInactive: boolean, time: string) => {
  let title = LOGOUT_MODAL.LOGOUT_TITLE;
  let description = LOGOUT_MODAL.LOGOUT_DESCRIPTION;
  let message = '';
  let firstBtn = LOGOUT_MODAL.CANCEL_BTN_LABEL;
  let secondBtn = LOGOUT_MODAL.LOGOUT_BTN_LABEL;
  let state: 'informative' | 'warning' = 'warning';

  if (!LocalStorageService.hasAuth()) {
    title = LOGOUT_MODAL.ALREADY_LOGOUT_TITLE;
    message = LOGOUT_MODAL.ALREADY_LOGOUT_MESSAGE;
    description = LOGOUT_MODAL.ALREADY_LOGOUT_DESCRIPTION;
    firstBtn = '';
    secondBtn = LOGOUT_MODAL.BACK_TO_HOMEPAGE_LABEL;
    state = 'informative';
  } else if (isInactive) {
    title = LOGOUT_MODAL.INACTIVITY_TITLE;
    description = LOGOUT_MODAL.INACTIVITY_DESCRIPTION;
    message = `You will be logged out in ${time} if there is no activity.`;
    firstBtn = LOGOUT_MODAL.LOGOUT_BTN_LABEL;
    secondBtn = LOGOUT_MODAL.CONTINUE_BTN_LABEL;
  }

  return {
    state,
    title,
    description,
    message,
    firstBtn,
    secondBtn,
  };
};

export const checkIndicator = () => {
  const { cspIndicator, gcIndicator, ipIndicator, orIndicator } =
    LocalStorageService.getAuthInfo(
      'cspIndicator',
      'gcIndicator',
      'ipIndicator',
      'orIndicator',
    );

  const indicators = { cspIndicator, gcIndicator, ipIndicator, orIndicator };

  return Object.values(indicators).includes('Y');
};

export const getDueStatus = (dueDate: string) => {
  const currentDate = new Date();
  const dueDateTime = new Date(dueDate);

  const timeDifference = dueDateTime.getTime() - currentDate.getTime();

  const dayDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  if (dayDifference < 0) {
    return `Overdue ${Math.abs(dayDifference)} day(s)`;
  } else {
    return `Due in ${dayDifference} day(s)`;
  }
};

export const addSpaces = (...args: string[]) => {
  return args.filter(Boolean).join(' ');
};

export const filterEmpty = (arr: (string | undefined)[]) =>
  arr.filter((val) => !!val?.trim());

export const formatAddress = ({
  blockNo = '',
  streetName = '',
  levelNo,
  unitNo,
  buildingName = '',
  postalCode,
  foreignAddr1,
  foreignAddr2,
}: AddressObject) => {
  if (foreignAddr1 || foreignAddr2)
    return filterEmpty([foreignAddr1, foreignAddr2]).join('\n');

  const unitAndLevel = filterEmpty([levelNo ? `#${levelNo}` : '', unitNo]).join(
    '-',
  );

  const country = filterEmpty([
    blockNo,
    streetName,
    unitAndLevel,
    buildingName,
    postalCode,
  ]).length
    ? `Singapore ${postalCode ?? ''}`
    : '';

  return filterEmpty([
    `${blockNo} ${streetName}`.trim(),
    `${unitAndLevel} ${buildingName}`.trim(),
    country.trim(),
  ]).join(', ');
};

export const formatPhoneNumberChunks = (mobileNo = '', digits = 4) =>
  filterEmpty(mobileNo.split(''))
    .map((digit, index) => ((index + 1) % digits === 0 ? `${digit} ` : digit))
    .join('');

export const formatMobileNumber = (mobileNo = '', countryCode?: string) => {
  const number = formatPhoneNumberChunks(mobileNo);

  if (!countryCode) return number;

  // Display mobile number only if country code is Singapore (65)
  return countryCode === '65'
    ? number
    : filterEmpty([`+${countryCode}`, number]).join(' ');
};

export const formatDateLabel = (inputDate: string | undefined): string => {
  if (!inputDate) return '-';
  const datePart = inputDate.split(' ')[0];
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const [year, month, day] = datePart.split('-');
  const formattedDate = `${day} ${months[parseInt(month, 10) - 1]} ${year}`;

  return formattedDate;
};

export const limitString = (string: string, maxLength = 50) => {
  if (string.length <= maxLength) {
    return string;
  } else {
    return string.substring(0, maxLength) + '...';
  }
};

export const renderButtons = (
  origin: string,
  cspIndicator: string,
  withProfile?: boolean,
) => {
  let index: number;

  if (origin !== 'CP') {
    index = 3;
  } else {
    index = withProfile ? 2 : 3;
  }

  if (
    (origin === 'CP' && !withProfile && cspIndicator === 'Y') ||
    origin === 'SP'
  ) {
    return TODO_LINKS.slice(0, 2);
  }

  return TODO_LINKS.filter((_, i) => i !== index);
};

export const isDateGreater = (newDate: string, oldDate: string) => {
  const newDateObj = new Date(newDate);
  const oldDateObj = new Date(oldDate);

  if (isNaN(newDateObj.getTime()) || isNaN(oldDateObj.getTime())) {
    return false;
  }

  return newDateObj.getTime() > oldDateObj.getTime();
};

export const checkIfShouldSfv = (pathname: string) => {
  const { origin, authLevel } = LocalStorageService.getAuthInfo(
    'origin',
    'authLevel',
  );

  return (
    sfv.transactions.some((url) => pathname.includes(url)) &&
    (!LocalStorageService.hasAuth() ||
      (authLevel === '1' && origin === SPCP_ORIGIN.SP))
  );
};

export const shouldRenderComponent = (url: MockURL): boolean => {
  const urls = urlsToHideComponent;
  const { pathname, search } = url;
  const searchParams = new URLSearchParams(search);

  return !urls.some(
    ({ path, queryParam }) =>
      pathname.includes(path) && searchParams.has(queryParam),
  );
};

export const scrollToElement = (elementId: string, margin = 0) => {
  const element = document.getElementById(elementId);

  if (element) {
    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - margin;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

export const decodeBase64 = (string: string) => {
  try {
    return atob(string);
  } catch (error) {
    console.error('Error in decoding base64:', error);
    return '';
  }
};
